import React, { useState, useEffect } from 'react';
import {
  CardElement,
  injectStripe,
  Elements,
  StripeProvider,
} from 'react-stripe-elements';
import { AxiosError } from 'axios';
import {
  TextField,
  DSMModal as Modal,
  ButtonBar,
  PrimaryButton,
} from '@weave/platform-ui-components';
import { expressApiKey } from '../../config';
import { CustomAxios } from '../../redux/axios/axios';

import * as styles from './virtual-terminal.component.styles';
import VisaIcon from '../../images/visa.png';
import DiscoverIcon from '../../images/discover.png';
import AmexIcon from '../../images/amex.png';
import MastercardIcon from '../../images/mastercard.png';
import { sendSuccess, iFrameMessages, sendUiLoaded } from './iframe-messages';
import { useInvoice } from './use-invoice.hook';

interface Payment {
  amount: number;
  token: string;
  cardName: string;
  receiptEmail: string;
}

// Override some Stripe styles
var cardFieldStyle = {
  base: {
    color: '#797E82',
    fontSize: '13px',
    '::placeholder': {
      color: '#797E82',
    },
  },
};

const VirtualTerminalInner = injectStripe(({ stripe }) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const invoice = useInvoice(setErrorMessage);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const [cardErrorMessage, setCardErrorMessage] = useState<string>();

  const [payment, setPayment] = React.useState<Payment>({
    amount: 0,
    cardName: '',
    receiptEmail: '',
    token: '',
  });

  const sendMessageToParent = (message: iFrameMessages) =>
    window.parent.postMessage(message, '*');

  useEffect(() => sendMessageToParent(sendUiLoaded()), []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPayment({ ...payment, [name]: value });
  };

  const handleCardChange = (event: any) => {
    if (submitAttempted) {
      setCardErrorMessage(event.error ? event.error.message : '');
    }
  };

  const validate = () => {
    return !!(payment.cardName && validateEmail());
  };

  // TODO: The TextField component doesn't handle type=email, so we're doing it manually here.
  const validateEmail = () => {
    return (
      payment.receiptEmail.length === 0 ||
      /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/.test(payment.receiptEmail)
    );
  };

  const confirmPayment = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe) {
      setErrorMessage('Error: Stripe not loaded');
      setSubmitAttempted(true);
      return;
    }

    if (validate()) {
      const result = await stripe.createToken({
        name: payment.cardName,
      });
      if (result.token) {
        submitPayment(result.token.id);
      } else {
        setCardErrorMessage(result?.error?.message ?? 'Processing Error');
        setSubmitAttempted(true);
      }
    }
  };

  const submitPayment = async (token: string) => {
    setIsSaving(true);
    CustomAxios.post(`invoices/${invoice?.id}/payments`, {
      ...payment,
      token,
      amount: invoice?.amount,
    })
      .then(() => {
        sendMessageToParent(sendSuccess(payment.receiptEmail));
      })
      .catch((error) => {
        setIsSaving(false);

        if (error && error.response && error.response.status === 402) {
          setErrorMessage(getDeclinedErrorMessage(error));
        } else {
          setErrorMessage(
            'Payment Failed. Please ensure all fields are filled out correctly and try again.'
          );
        }
      });
  };

  const getDeclinedErrorMessage = (error: AxiosError) => {
    const defaultMessage = 'The credit card you entered has been declined.';

    const message = error.response?.data?.error?.message;

    // message can be undefined or an empty string
    return !!message ? message : defaultMessage;
  };

  return (
    <main>
      {invoice && (
        <form onSubmit={confirmPayment} className={styles.container}>
          <TextField
            name="cardName"
            label="Cardholder's Name"
            value={payment.cardName}
            onChange={handleChange}
            className={styles.nameField}
          />
          <CardElement
            className={styles.cardField}
            style={cardFieldStyle}
            onChange={handleCardChange}
          />
          {cardErrorMessage && (
            <span className={styles.errorText}>{cardErrorMessage}</span>
          )}
          <div className={styles.cardLogos}>
            <img src={VisaIcon} alt="Visa" />
            <img src={MastercardIcon} alt="Mastercard" />
            <img src={DiscoverIcon} alt="Discover" />
            <img src={AmexIcon} alt="American Express" />
          </div>
          <TextField
            name="receiptEmail"
            label="Email Receipt (Optional)"
            value={payment.receiptEmail}
            onChange={handleChange}
            className={styles.emailField}
            // TODO: The Text Field action buttons submit the form. We need to fix that before adding this.
            // actionText="Clear"
            // onActionClick={() => (payment.receiptEmail = '')}
          />
          {!validateEmail() && (
            <span className={styles.errorText}>Invalid email address</span>
          )}
          <ButtonBar className={styles.buttonWrapper}>
            <PrimaryButton type="submit" disabled={!validate() || isSaving}>
              Process Payment
            </PrimaryButton>
          </ButtonBar>
        </form>
      )}

      <Modal open={!!errorMessage} onClose={() => setErrorMessage(undefined)}>
        <div className={styles.modalStyles}>
          <p>{errorMessage}</p>
          <PrimaryButton onClick={() => setErrorMessage(undefined)}>Close</PrimaryButton>
        </div>
      </Modal>
    </main>
  );
});

export const VirtualTerminal = () => {
  return (
    <StripeProvider apiKey={expressApiKey}>
      <Elements>
        <VirtualTerminalInner />
      </Elements>
    </StripeProvider>
  );
};
